import Component from '@glimmer/component';
import Drawer from 'tio-ui/components/drawer';
import ProgramReadonlyField from 'tio-common/components/tuition-assistance/program-readonly-field';
import ProgramCustomReadonlyField from 'tio-common/components/tuition-assistance/program-custom-readonly-field';
import ExpandableText from 'tio-common/components/expandable-text';
import { divide } from 'tio-ui/math';
import { on } from '@ember/modifier';
import { eq } from 'tio-ui/utilities';
import type TasCourse from 'tio-data/models/tas-course';
import type TasProgramTemplateModel from 'tio-data/models/tas-program-template';
import safeFormatNumber from 'tio-common/helpers/safe-format-number';
import { PhX } from 'ember-phosphor-icons';
import { t } from 'ember-intl';

const number = Number;

interface TasShowCourseDrawerArgs {
  Args: {
    isOpen: boolean;
    onClose: () => void;
    course: TasCourse | null;
    programTemplate: TasProgramTemplateModel;
    header: string;
  };
  Element: HTMLElement;
}

export default class TasShowCourseDrawer extends Component<TasShowCourseDrawerArgs> {
  expenseFields = [
    'COURSE_TUITION',
    'COURSE_BOOKS',
    'COURSE_LABS',
    'COURSE_SOFTWARE',
    'COURSE_FEES',
    'APPROVED_COURSE_TOTAL',
  ] as const;

  get allCourseFields() {
    return this.args.course ? Object.keys(this.args.course.fields) : [];
  }

  get detailFields() {
    return this.allCourseFields.filter(
      (field) => !this.expenseFields.includes(field as (typeof this.expenseFields)[number])
    );
  }

  <template>
    <Drawer
      @isOpen={{@isOpen}}
      @onClose={{@onClose}}
      @allowCloseButton={{false}}
      @size="xl"
      @placement="right"
      @closeOnOutsideClick={{false}}
      @closeOnEscapeKey={{true}}
    >
      <:header>
        <div class="tio-row-between">
          <h2 class="tio-text-lg-semibold tio-fg-primary">
            {{@header}}
          </h2>
          <button
            type="button"
            data-test-close-education-drawer
            {{on "click" @onClose}}
            class="ml-4"
          >
            <PhX @size="24" />
          </button>
        </div>
      </:header>

      <:body>
        {{#if @course}}
          <div class="px-6 py-7 tio-col-extra-loose">
            {{! Expenses Section }}
            <div class="tio-col-flush">
              {{! Section Header }}
              <div
                class="tio-row-between px-4 py-2.5 bg-gray-100/50 border border-gray-200/80 rounded-[10px] mb-0"
              >
                <span class="tio-text-base-semibold tio-fg-primary">{{t
                    "tuition_assistance.course_drawer.expenses"
                  }}</span>
              </div>

              {{! Expense Rows }}
              <div class="mt-2 space-y-2">
                {{#each this.expenseFields as |fieldName|}}
                  <ProgramReadonlyField
                    {{! @glint-ignore: readonly array iteration - APPROVED_COURSE_TOTAL not in field signature }}
                    @name={{fieldName}}
                    @programTemplate={{@programTemplate}}
                    @fields={{@course.fields}}
                    as |field|
                  >
                    {{#if field.value}}
                      <div
                        class="tio-row-between p-4
                          {{if
                            (eq fieldName 'APPROVED_COURSE_TOTAL')
                            ''
                            'border-b tio-border-default'
                          }}"
                      >
                        <span
                          class="text-base
                            {{if
                              (eq fieldName 'APPROVED_COURSE_TOTAL')
                              'font-bold tio-fg-secondary'
                              'font-medium tio-fg-secondary'
                            }}"
                        >{{field.programLabel}}</span>
                        <span
                          class="text-base
                            {{if
                              (eq fieldName 'APPROVED_COURSE_TOTAL')
                              'font-semibold tio-fg-brand'
                              'font-medium tio-fg-secondary'
                            }}"
                        >{{safeFormatNumber
                            (divide (number field.value) 100)
                            style="currency"
                            currency="USD"
                          }}</span>
                      </div>
                    {{/if}}
                  </ProgramReadonlyField>
                {{/each}}
              </div>
            </div>

            {{! Details Section }}
            <div class="tio-col-flush">
              {{! Section Header }}
              <div
                class="tio-row-between px-4 py-2.5 bg-gray-100/50 border border-gray-200/80 rounded-[10px] mb-0"
              >
                <span class="tio-text-base-semibold tio-fg-primary">{{t
                    "tuition_assistance.course_drawer.details"
                  }}</span>
              </div>

              {{! Detail Rows }}
              <div class="mt-2">
                {{#each this.detailFields as |fieldName|}}
                  <ProgramReadonlyField
                    {{! @glint-ignore: Dynamic field name from string array }}
                    @name={{fieldName}}
                    @programTemplate={{@programTemplate}}
                    @fields={{@course.fields}}
                    as |field|
                  >
                    {{#if field.value}}
                      <div
                        class="tio-col-flush gap-0.5 p-4 border-b tio-border-default last:border-b-0"
                      >
                        <span
                          class="tio-text-base-semibold tio-fg-secondary leading-snug"
                        >{{field.programLabel}}</span>
                        <ExpandableText
                          {{! @glint-ignore: Field value type }}
                          @text={{field.value}}
                          class="tio-text-base-medium tio-fg-muted leading-snug"
                        />
                      </div>
                    {{/if}}
                  </ProgramReadonlyField>
                {{/each}}

                {{! Custom Fields }}
                {{#each @course.customFields as |customField|}}
                  <ProgramCustomReadonlyField @customField={{customField}} as |field|>
                    <div
                      class="tio-col-flush gap-0.5 p-4 border-b tio-border-default last:border-b-0"
                    >
                      <span
                        class="tio-text-base-semibold tio-fg-secondary leading-snug"
                        {{! @glint-ignore: Custom field yielded type is generic }}
                      >{{field.programLabel}}</span>
                      <ExpandableText
                        {{! @glint-ignore: field.value type }}
                        @text={{field.value}}
                        class="tio-text-base-medium tio-fg-muted leading-snug"
                      />
                    </div>
                  </ProgramCustomReadonlyField>
                {{/each}}
              </div>
            </div>
          </div>
        {{/if}}
      </:body>
    </Drawer>
  </template>
}
